import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { formatApiError } from "@/lib/format-api-error";
import { queryKeys } from "@/lib/query-keys";

// POST /api/admin/listing-changes/:id/approve
//
// Pessimistic (MDF use-approve-request convention) — approve is the moment
// the worker writes the live marketplace row, and it can fail server-side
// (409 already_processed from a parallel reviewer; 400 stale_link /
// marketplace_not_configured / marketplace_write_failed). Rolling an
// optimistic patch back would briefly flash an "approved" state that never
// applied.
//
// Invalidates `listingChanges.all` — the bare prefix sweeps every status
// filter cache plus the open drawer detail. On 409 the request was decided
// by someone else, so we also invalidate to pull the fresh decision in.
type Vars = { id: string };

export function useApproveListingChange() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, Vars>({
    mutationFn: ({ id }) =>
      apiFetch<{ ok: true }>(`/admin/listing-changes/${id}/approve`, {
        method: "POST",
      }),
    onSuccess: () => {
      toast.success("Change approved — listing updated");
      void queryClient.invalidateQueries({
        queryKey: queryKeys.admin.listingChanges.all,
      });
    },
    onError: (err) => {
      if (err.status === 409) {
        toast.error("Already processed — refreshing");
        void queryClient.invalidateQueries({
          queryKey: queryKeys.admin.listingChanges.all,
        });
        return;
      }
      toast.error(formatApiError(err, "Failed to approve change"));
    },
  });
}
